/**
 * Export Button Component
 *
 * Provides beautiful, branded PDF and CSV export functionality for link analytics.
 * Features modern design with charts, progress bars, and visual hierarchy.
 *
 * @module components/export-button
 */

import { useState } from 'react';
import { Download, FileText, FileSpreadsheet, Loader2, ChevronDown } from 'lucide-react';
import { formatNumber, formatDate } from '@/client/lib/utils';
import { countryCodeToFlag, getCountryName } from '@/client/lib/country-utils';
import { useClickOutside } from '@/client/hooks/use-click-outside';

// Type for jsPDF with autoTable plugin (adds lastAutoTable property)
// Note: jsPDF is now dynamically imported, so we need to type it more flexibly
type JsPDFWithAutoTable = any & {
  lastAutoTable?: { finalY: number };
};

// =============================================================================
// Brand Colors (matching app's chart colors)
// =============================================================================

const COLORS = {
  // Primary brand colors
  amber: { r: 244, g: 162, b: 10 }, // hsl(38, 92%, 50%) - Primary
  teal: { r: 31, g: 173, b: 153 }, // hsl(172, 70%, 40%) - Secondary
  purple: { r: 118, g: 46, b: 224 }, // hsl(262, 85%, 55%) - Accent
  gold: { r: 248, g: 198, b: 6 }, // hsl(45, 95%, 50%) - Highlight
  pink: { r: 244, g: 10, b: 91 }, // hsl(339, 92%, 50%) - Emphasis

  // Neutral colors
  dark: { r: 30, g: 30, b: 35 },
  gray: { r: 100, g: 100, b: 105 },
  lightGray: { r: 248, g: 248, b: 252 },
  white: { r: 255, g: 255, b: 255 },
};

interface DailyStat {
  date: string;
  clicks: number;
  uniqueVisitors?: number;
  referrers?: Record<string, number>;
  countries?: Record<string, number>;
  cities?: Record<string, number>;
  devices?: Record<string, number>;
  browsers?: Record<string, number>;
  os?: Record<string, number>;
  referrerCategories?: Record<string, number>;
  referrerPlatforms?: Record<string, number>;
}

interface LinkData {
  shortCode: string;
  shortUrl: string;
  destinationUrl: string;
  title?: string | null;
  totalClicks: number;
  createdAt: string;
}

interface ExportButtonProps {
  link: LinkData;
  dailyStats: DailyStat[];
  startDate: string;
  endDate: string;
}

export function ExportButton({ link, dailyStats, startDate, endDate }: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ⚡ Performance Optimization: Use shared click-outside hook instead of manual listener
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false), isOpen);

  const handleExportCSV = () => {
    setIsExporting(true);
    setIsOpen(false);

    try {
      const csv = generateCSV(link, dailyStats, startDate, endDate);
      downloadFile(csv, `${link.shortCode}-analytics-${startDate}-to-${endDate}.csv`, 'text/csv');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setIsOpen(false);

    try {
      await generatePDF(link, dailyStats, startDate, endDate);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className="p-2.5 rounded-xl border bg-card hover:bg-muted transition-all flex items-center gap-1.5"
        aria-label="Export analytics"
        title="Export analytics"
      >
        {isExporting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            <Download className="h-5 w-5" />
            <ChevronDown className="h-3 w-3" />
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 rounded-lg border bg-card shadow-lg z-10 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-1">
            <button
              onClick={handleExportCSV}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4 text-green-500" />
              <div className="text-left">
                <p className="font-medium">CSV</p>
                <p className="text-xs text-muted-foreground">For spreadsheets</p>
              </div>
            </button>
            <button
              onClick={handleExportPDF}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors"
            >
              <FileText className="h-4 w-4 text-red-500" />
              <div className="text-left">
                <p className="font-medium">PDF</p>
                <p className="text-xs text-muted-foreground">For sharing</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function downloadFile(content: string | Blob, filename: string, mimeType: string) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function generateCSV(
  link: LinkData,
  dailyStats: DailyStat[],
  startDate: string,
  endDate: string
): string {
  const lines: string[] = [];

  // Header info
  lines.push(`Link Analytics Report`);
  lines.push(`Short URL,${link.shortUrl}`);
  lines.push(`Destination,${link.destinationUrl}`);
  lines.push(`Date Range,${formatDate(startDate)} - ${formatDate(endDate)}`);
  lines.push(`Total Clicks,${link.totalClicks}`);
  lines.push(``);

  // Daily clicks
  lines.push(`Daily Clicks`);
  lines.push(`Date,Clicks,Unique Visitors`);
  dailyStats.forEach((stat) => {
    lines.push(`${formatDate(stat.date)},${stat.clicks},${stat.uniqueVisitors ?? 0}`);
  });
  lines.push(``);

  // ⚡ Performance: Aggregate all stats in a single iteration
  const aggregated = aggregateAllStats(dailyStats);

  // Top referrers
  if (Object.keys(aggregated.referrers).length > 0) {
    lines.push(`Top Referrers`);
    lines.push(`Source,Clicks`);
    Object.entries(aggregated.referrers)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .forEach(([source, clicks]) => {
        lines.push(`"${source}",${clicks}`);
      });
    lines.push(``);
  }

  // Visitors by country
  if (Object.keys(aggregated.countries).length > 0) {
    lines.push(`Visitors by Country`);
    lines.push(`Country,Clicks`);
    Object.entries(aggregated.countries)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .forEach(([country, clicks]) => {
        lines.push(`"${country}",${clicks}`);
      });
    lines.push(``);
  }

  // Devices
  if (Object.keys(aggregated.devices).length > 0) {
    lines.push(`Devices`);
    lines.push(`Device,Clicks`);
    Object.entries(aggregated.devices)
      .sort((a, b) => b[1] - a[1])
      .forEach(([device, clicks]) => {
        lines.push(`"${device}",${clicks}`);
      });
    lines.push(``);
  }

  // Browsers
  if (Object.keys(aggregated.browsers).length > 0) {
    lines.push(`Browsers`);
    lines.push(`Browser,Clicks`);
    Object.entries(aggregated.browsers)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .forEach(([browser, clicks]) => {
        lines.push(`"${browser}",${clicks}`);
      });
    lines.push(``);
  }

  // Operating Systems
  if (Object.keys(aggregated.os).length > 0) {
    lines.push(`Operating Systems`);
    lines.push(`OS,Clicks`);
    Object.entries(aggregated.os)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .forEach(([osName, clicks]) => {
        lines.push(`"${osName}",${clicks}`);
      });
  }

  return lines.join('\n');
}

// =============================================================================
// PDF Drawing Helpers
// =============================================================================

/**
 * Draw a mini bar chart for trend visualization
 */
function drawMiniBarChart(
  doc: any, // jsPDF instance (dynamically imported)
  x: number,
  y: number,
  width: number,
  height: number,
  data: number[],
  color: { r: number; g: number; b: number }
) {
  if (data.length === 0) return;

  const max = Math.max(...data, 1);
  const barWidth = Math.min((width - (data.length - 1) * 1) / data.length, 8);
  const gap = 1;

  // Draw background
  doc.setFillColor(COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b);
  doc.roundedRect(x - 2, y - 2, width + 4, height + 4, 2, 2, 'F');

  // Draw bars
  data.forEach((value, i) => {
    const barHeight = Math.max((value / max) * height, 1);
    const barX = x + i * (barWidth + gap);
    const barY = y + height - barHeight;

    // Bar with slight gradient effect (darker at bottom)
    doc.setFillColor(color.r, color.g, color.b);
    doc.roundedRect(barX, barY, barWidth, barHeight, 1, 1, 'F');
  });
}

/**
 * Draw a stat card with icon placeholder
 */
function drawStatCard(
  doc: any, // jsPDF instance (dynamically imported)
  x: number,
  y: number,
  width: number,
  height: number,
  value: string,
  label: string,
  color: { r: number; g: number; b: number },
  _icon?: string
) {
  // Card background with subtle border effect
  doc.setFillColor(color.r, color.g, color.b);
  doc.roundedRect(x, y, width, height, 4, 4, 'F');

  // Value (large text)
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(value, x + width / 2, y + height / 2 - 2, { align: 'center' });

  // Label (small text)
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(label, x + width / 2, y + height / 2 + 8, { align: 'center' });
}

async function generatePDF(
  link: LinkData,
  dailyStats: DailyStat[],
  startDate: string,
  endDate: string
) {
  // ⚡ Performance Optimization: Lazy load PDF libraries (250KB) only when export is triggered
  // This removes the libraries from the main bundle, improving initial page load
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const doc: JsPDFWithAutoTable = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Calculate totals
  const totalClicks = dailyStats.reduce((sum, s) => sum + s.clicks, 0);
  const totalUnique = dailyStats.reduce((sum, s) => sum + (s.uniqueVisitors ?? 0), 0);
  const avgPerDay = dailyStats.length > 0 ? Math.round(totalClicks / dailyStats.length) : 0;

  // ==========================================================================
  // Header Section - Branded Design
  // ==========================================================================

  // Header background gradient effect (using multiple rectangles)
  doc.setFillColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
  doc.rect(0, 0, pageWidth, 45, 'F');

  // Accent stripe
  doc.setFillColor(COLORS.amber.r, COLORS.amber.g, COLORS.amber.b);
  doc.rect(0, 45, pageWidth, 3, 'F');

  // Brand name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('ARMENIAN LINK', 14, 15);

  // Title
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Link Analytics Report', 14, 30);

  // Date info (right aligned)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 180, 185);
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
    pageWidth - 14,
    15,
    { align: 'right' }
  );
  doc.text(`${formatDate(startDate)} - ${formatDate(endDate)}`, pageWidth - 14, 22, {
    align: 'right',
  });

  // ==========================================================================
  // Link Info Card
  // ==========================================================================

  const linkCardY = 55;
  doc.setFillColor(COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b);
  doc.roundedRect(14, linkCardY, pageWidth - 28, 28, 4, 4, 'F');

  // Short URL (prominent)
  doc.setTextColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(link.shortUrl, 20, linkCardY + 12);

  // Destination URL (truncated)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(COLORS.gray.r, COLORS.gray.g, COLORS.gray.b);
  const maxDestLength = 90;
  const truncatedDest =
    link.destinationUrl.length > maxDestLength
      ? `${link.destinationUrl.substring(0, maxDestLength)}…`
      : link.destinationUrl;
  doc.text(`→ ${truncatedDest}`, 20, linkCardY + 22);

  // ==========================================================================
  // Stat Cards Row
  // ==========================================================================

  const statsY = 92;
  const cardWidth = 56;
  const cardHeight = 32;
  const cardGap = 8;

  drawStatCard(
    doc,
    14,
    statsY,
    cardWidth,
    cardHeight,
    formatNumber(totalClicks),
    'Total Clicks',
    COLORS.amber
  );
  drawStatCard(
    doc,
    14 + cardWidth + cardGap,
    statsY,
    cardWidth,
    cardHeight,
    formatNumber(totalUnique),
    'Unique Visitors',
    COLORS.teal
  );
  drawStatCard(
    doc,
    14 + (cardWidth + cardGap) * 2,
    statsY,
    cardWidth,
    cardHeight,
    formatNumber(avgPerDay),
    'Avg Per Day',
    COLORS.purple
  );

  // ==========================================================================
  // Mini Trend Chart
  // ==========================================================================

  const chartY = statsY + 5;
  const chartX = 14 + (cardWidth + cardGap) * 3 + 4;
  const chartWidth = pageWidth - chartX - 14;
  const chartHeight = 22;

  // Get last 14 days of data for mini chart
  const recentData = dailyStats.slice(-14).map((s) => s.clicks);

  if (recentData.length > 0) {
    // Chart label
    doc.setFontSize(8);
    doc.setTextColor(COLORS.gray.r, COLORS.gray.g, COLORS.gray.b);
    doc.text('TREND', chartX, chartY - 2);

    drawMiniBarChart(doc, chartX, chartY + 2, chartWidth, chartHeight, recentData, COLORS.amber);
  }

  let yPos = statsY + cardHeight + 18;

  // ==========================================================================
  // Daily Performance Table
  // ==========================================================================

  if (dailyStats.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
    doc.text('Daily Performance', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Clicks', 'Visitors', 'Trend']],
      body: dailyStats.slice(0, 12).map((stat, idx, arr) => {
        const prev = idx > 0 ? (arr[idx - 1]?.clicks ?? 0) : stat.clicks;
        const trend = stat.clicks > prev ? '↑' : stat.clicks < prev ? '↓' : '→';
        return [
          formatDate(stat.date),
          formatNumber(stat.clicks),
          formatNumber(stat.uniqueVisitors ?? 0),
          trend,
        ];
      }),
      theme: 'plain',
      headStyles: {
        fillColor: [COLORS.amber.r, COLORS.amber.g, COLORS.amber.b],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: {
        fillColor: [COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b],
      },
      columnStyles: {
        0: { cellWidth: 50 },
        3: { halign: 'center', cellWidth: 20 },
      },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 12;
  }

  // ==========================================================================
  // Aggregate Stats (Performance: Single iteration instead of 4 separate ones)
  // ==========================================================================

  const aggregated = aggregateAllStats(dailyStats);

  // ==========================================================================
  // Top Referrers with Progress Bars
  // ==========================================================================

  const topReferrers = Object.entries(aggregated.referrers)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  if (topReferrers.length > 0 && yPos < 220) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
    doc.text('Top Referrers', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Source', 'Clicks', 'Share']],
      body: topReferrers.map(([source, clicks]) => [
        source.length > 35 ? `${source.substring(0, 35)}…` : source,
        formatNumber(clicks),
        `${((clicks / totalClicks) * 100).toFixed(1)}%`,
      ]),
      theme: 'plain',
      headStyles: {
        fillColor: [COLORS.purple.r, COLORS.purple.g, COLORS.purple.b],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: {
        fillColor: [COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b],
      },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 12;
  }

  // ==========================================================================
  // Countries with Flags
  // ==========================================================================

  const topCountries = Object.entries(aggregated.countries)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  if (topCountries.length > 0) {
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
    doc.text('Top Countries', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Country', 'Clicks', 'Share']],
      body: topCountries.map(([code, clicks]) => {
        const flag = countryCodeToFlag(code);
        const name = getCountryName(code);
        return [
          `${flag} ${name}`,
          formatNumber(clicks),
          `${((clicks / totalClicks) * 100).toFixed(1)}%`,
        ];
      }),
      theme: 'plain',
      headStyles: {
        fillColor: [COLORS.teal.r, COLORS.teal.g, COLORS.teal.b],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: {
        fillColor: [COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b],
      },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 12;
  }

  // ==========================================================================
  // Devices Breakdown
  // ==========================================================================

  const deviceData = Object.entries(aggregated.devices)
    .filter(([name]) => name.toLowerCase() !== 'mobile' && name.toLowerCase() !== 'desktop')
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  if (deviceData.length > 0) {
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
    doc.text('Devices', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Device', 'Clicks', 'Share']],
      body: deviceData.map(([device, clicks]) => [
        device,
        formatNumber(clicks),
        `${((clicks / totalClicks) * 100).toFixed(1)}%`,
      ]),
      theme: 'plain',
      headStyles: {
        fillColor: [COLORS.gold.r, COLORS.gold.g, COLORS.gold.b],
        textColor: [COLORS.dark.r, COLORS.dark.g, COLORS.dark.b],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: {
        fillColor: [COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b],
      },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc.lastAutoTable?.finalY ?? yPos) + 12;
  }

  // ==========================================================================
  // Browsers
  // ==========================================================================

  const browserData = Object.entries(aggregated.browsers)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  if (browserData.length > 0) {
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.dark.r, COLORS.dark.g, COLORS.dark.b);
    doc.text('Browsers', 14, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      head: [['Browser', 'Clicks', 'Share']],
      body: browserData.map(([browser, clicks]) => [
        browser,
        formatNumber(clicks),
        `${((clicks / totalClicks) * 100).toFixed(1)}%`,
      ]),
      theme: 'plain',
      headStyles: {
        fillColor: [COLORS.pink.r, COLORS.pink.g, COLORS.pink.b],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      alternateRowStyles: {
        fillColor: [COLORS.lightGray.r, COLORS.lightGray.g, COLORS.lightGray.b],
      },
      margin: { left: 14, right: 14 },
    });
  }

  // ==========================================================================
  // Footer on all pages
  // ==========================================================================

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Footer line
    doc.setDrawColor(COLORS.amber.r, COLORS.amber.g, COLORS.amber.b);
    doc.setLineWidth(0.5);
    doc.line(14, 285, pageWidth - 14, 285);

    // Footer text
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray.r, COLORS.gray.g, COLORS.gray.b);
    doc.text('Armenian Link', 14, 291);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 291, { align: 'center' });
    doc.text('arml.ink', pageWidth - 14, 291, { align: 'right' });
  }

  // Save
  doc.save(`${link.shortCode}-analytics-${startDate}-to-${endDate}.pdf`);
}

/**
 * ⚡ Performance Optimization: Aggregate all stats in a single iteration
 * instead of iterating 9 times (once per stat type)
 * Reduces complexity from 9×O(n) to O(n)
 */
function aggregateAllStats(dailyStats: DailyStat[]) {
  const result = {
    referrers: {} as Record<string, number>,
    countries: {} as Record<string, number>,
    cities: {} as Record<string, number>,
    devices: {} as Record<string, number>,
    browsers: {} as Record<string, number>,
    os: {} as Record<string, number>,
    referrerCategories: {} as Record<string, number>,
    referrerPlatforms: {} as Record<string, number>,
  };

  // Single iteration over dailyStats
  dailyStats.forEach((stat) => {
    // Aggregate referrers
    if (stat.referrers) {
      Object.entries(stat.referrers).forEach(([name, count]) => {
        result.referrers[name] = (result.referrers[name] || 0) + count;
      });
    }
    // Aggregate countries
    if (stat.countries) {
      Object.entries(stat.countries).forEach(([name, count]) => {
        result.countries[name] = (result.countries[name] || 0) + count;
      });
    }
    // Aggregate cities
    if (stat.cities) {
      Object.entries(stat.cities).forEach(([name, count]) => {
        result.cities[name] = (result.cities[name] || 0) + count;
      });
    }
    // Aggregate devices
    if (stat.devices) {
      Object.entries(stat.devices).forEach(([name, count]) => {
        result.devices[name] = (result.devices[name] || 0) + count;
      });
    }
    // Aggregate browsers
    if (stat.browsers) {
      Object.entries(stat.browsers).forEach(([name, count]) => {
        result.browsers[name] = (result.browsers[name] || 0) + count;
      });
    }
    // Aggregate os
    if (stat.os) {
      Object.entries(stat.os).forEach(([name, count]) => {
        result.os[name] = (result.os[name] || 0) + count;
      });
    }
    // Aggregate referrerCategories
    if (stat.referrerCategories) {
      Object.entries(stat.referrerCategories).forEach(([name, count]) => {
        result.referrerCategories[name] = (result.referrerCategories[name] || 0) + count;
      });
    }
    // Aggregate referrerPlatforms
    if (stat.referrerPlatforms) {
      Object.entries(stat.referrerPlatforms).forEach(([name, count]) => {
        result.referrerPlatforms[name] = (result.referrerPlatforms[name] || 0) + count;
      });
    }
  });

  return result;
}
